import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useInfiniteScroll } from 'ahooks';
import { useDispatch, useSelector } from 'react-redux';
import { groupBy } from 'lodash';
import { Spin } from 'antd';
// import Footer from '@/business-components/footer';
import Layout from '@/layouts';
import { ConSearchItemWebController } from '@/services/swagger/ConSearchItemWebController';
import ServerSideFetch from '@/utils/server-side-fetch';
import { ConBrandWebController } from '@/services/swagger/ConBrandWebController';
import SortMenu from '@/business-components/SortMenu';
import BrandCard from '@/business-components/BrandCard';
import type { ConBrandWebV1ListResponse, ConSearchItemWebV1ListResponse } from '@/services/swagger/data-contracts';
import { RootState } from '@/models';
import SelectTag from './components/selected-tag';
import Filter from './components/filter';
import styles from './index.module.scss';

const controller = new ConSearchItemWebController();
const brandController = new ConBrandWebController();
const pageSize = 15;

export type IndexPageServiderProps = {
  searchItemList: ConSearchItemWebV1ListResponse;
  initialBrandLibraryList: ConBrandWebV1ListResponse[];
  brandLibraryTotal: number | string;
  includeBrandTotalCount: number | string;
};

export const getServerSideProps: GetServerSideProps<IndexPageServiderProps> = async ({ res }) => {
  res.setHeader('Cache-Control', 'public, s-maxage=15, stale-while-revalidate=59');
  const { data } = await ServerSideFetch(brandController.conBrandWebControllerV1BrandCountUsingGet1);
  const searchItem = await ServerSideFetch(controller.conSearchItemWebControllerV1ListUsingGet1);
  const brandLibrary = await ServerSideFetch(brandController.conBrandWebControllerV1ListUsingGet1, {
    pageIndex: 1,
    pageSize,
    orderType: 'first_word_asc',
  });
  return {
    props: {
      searchItemList: {
        ...searchItem?.data,
        brandRanking: searchItem?.data?.brandRanking?.map(item => ({
          ...item,
          searchItemName: `${item?.searchItemName}%`,
        })),
      },
      initialBrandLibraryList: brandLibrary.data?.list || [],
      brandLibraryTotal: data?.brandTotalCount || '300+',
      includeBrandTotalCount: data?.includeBrandTotalCount || '10000+',
    },
  };
};

function BrandPage({
  searchItemList,
  initialBrandLibraryList,
  brandLibraryTotal,
  includeBrandTotalCount,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [loading, setLoading] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(true);
  const dispatch = useDispatch();
  const { filterData, orderType } = useSelector<RootState, RootState['brand']>(state => state.brand);
  const searchGroup = useMemo(() => groupBy(filterData, 'type'), [filterData]);
  const {
    data = { list: initialBrandLibraryList },
    // loading,
    loadMore,
    loadingMore,
    reload,
  } = useInfiniteScroll(
    async d => {
      const r = await brandController.conBrandWebControllerV1ListUsingGet1({
        ...(searchGroup.hot_city && { cityList: searchGroup.hot_city.map(item => item.name) }), // 所在地区/城市
        ...(searchGroup.industry && { oneTrackIdList: searchGroup.industry.map(item => item.id as string) }), // 赛道
        ...(searchGroup.finance_rounds && { financeRoundsList: searchGroup.finance_rounds.map(item => item.name) }), // 融资轮次
        ...(searchGroup.feature_label && { featureLabelList: searchGroup.feature_label.map(item => item.name) }), // 特色标签
        ...(searchGroup.brand_ranking && { rankingRateInIndustry: searchGroup.brand_ranking[0].name.replace('%', '') }), // 品牌排名
        ...(searchGroup.last_finance_year && {
          lastFinanceYearList: searchGroup.last_finance_year.map(item => item.name),
        }), // 最新获投
        ...(searchGroup.last_finance_year_max && {
          lastFinanceYearRangeMax: +searchGroup.last_finance_year_max[0].date,
        }), // 最新获投区间最大值
        ...(searchGroup.last_finance_year_min && {
          lastFinanceYearRangeMin: +searchGroup.last_finance_year_min[0].date,
        }), // 最新获投区间最小值
        ...(searchGroup.company_create_year && {
          companyCreateYearList: searchGroup.company_create_year.map(item => item.name),
        }), // 成立时间
        orderType,
        pageIndex: Math.floor((d?.list?.length || 0) / pageSize) + 1 || 1,
        pageSize,
      });
      return {
        list: r.data.data?.list || [],
        total: r.data.data?.total || 0,
      };
    },
    {
      // manual: true,
      // target: typeof window === undefined ? null : document.body,
      // reloadDeps: [orderType, searchGroup],
      isNoMore: d => {
        return d ? d.list.length >= d.total : false;
      },
      onFinally() {
        setLoading(false);
      },
    }
  );
  const handleScroll = (e: Event) => {
    if (loadingMore) {
      return;
    }

    const scrollTop =
      (e.target && (e.target as Document).documentElement.scrollTop) ||
      window.scrollY ||
      (e.target && (e.target as Document).body.scrollTop) ||
      0;
    const scrollHeight =
      (e.target && (e.target as Document).documentElement.scrollHeight) ||
      (e.target && (e.target as Document).body.scrollHeight) ||
      0;
    const windowHeight =
      document.compatMode === 'CSS1Compat' ? document.documentElement.clientHeight : document.body.clientHeight;
    if (scrollTop === 0) {
      setReloadFlag(true);
    }
    if (reloadFlag && scrollHeight - scrollTop - windowHeight < 500) {
      loadMore();
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });
  useEffect(() => {
    setLoading(true);
    setReloadFlag(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    const scrollTop = window.pageYOffset || window.scrollY || document.body.scrollTop;
    if (!scrollTop) {
      setReloadFlag(true);
    }
    // console.log(scrollTop);
    // setTimeout(() => {
    reload();
    // }, 1000);
  }, [reload, filterData, orderType]);
  return (
    <>
      <Layout
        title="中国新消费品牌库-新消费品牌列表 | CBNData品牌数集"
        description="品牌库 | CBNData品牌数集是第一财经商业数据中心（CBNData）旗下一款衡量品牌综合实力的可视化数据工具，基于CBNData沉淀的中国新消费品牌增长力评估模型与数据能力，从品牌赛道潜力、组织能力、经营能力、产品竞争力、营销能力、供应链能力、用户发展能力等7个维度，系统化帮助投资人及品牌创始人衡量品牌综合实力。品牌库覆盖17个行业并支持按照行业、融资轮次、成立时间等标签进行筛选，同时该品牌库包含近3000个品牌且每月持续针对热门中国新消费品牌进行收录和数据更新。"
        keyword="品牌、数据库、品牌库、品牌列表、消费、新消费、行业、赛道、融资轮次、成立时间、标签、品牌数集、CBNData、CBN、第一财经"
        isShowNavBar
        isFrapMain
      >
        <div className={classNames('flex-row-nowrap', styles.content)}>
          <div className={classNames(styles.leftContent)}>
            {/*  @ts-expect-error @todo 请修正 */}
            <Filter {...(searchItemList || {})} noData={!data?.list?.length} />
          </div>
          <div className={styles.rightContent}>
            <div className={styles.actionBar}>
              <SelectTag />
              <SortMenu
                title={
                  <span className={styles.totalBrandNumText}>
                    当前已收录<span className="reddish-orange-text-18">{includeBrandTotalCount}</span>品牌
                    <span className={styles.otherTips}>
                      （Beta版仅对外披露
                      {brandLibraryTotal}个品牌详情数据）
                    </span>
                  </span>
                }
                titleIconXLinkHref="icon-medal"
                selectedLabelValue={orderType}
                onSortLabelClick={value => {
                  dispatch({ type: 'brand/setOrderType', payload: value });
                }}
              />
            </div>
            <Spin spinning={loading}>
              {data?.list?.length ? (
                <div className={styles.gallery}>
                  {data?.list.map(item => {
                    return <BrandCard className={styles.brandCard} key={item.conBrandId} {...item} />;
                  })}
                </div>
              ) : (
                <div className={styles.nodata}>
                  <img src="//assets-oss.cbndata.com/cbndata-consume-brand/FkqsipYDfYGdRHO0uuYzTKILKXOn.png" />
                  <div className={styles.nodata__text}>未搜索到符合筛选条件的品牌，请尝试更换其他筛选条件</div>
                </div>
              )}
            </Spin>
          </div>
        </div>
      </Layout>
    </>
  );
}

export default BrandPage;
